import React from 'react';
import {Box, Typography, Grid, Tooltip} from '@mui/material';
import ClassDashboardShell from '../components/ClassDashboard.comp';
import NoDataAvailable from '../../../components/NoDataAvailable';
import CircularLoader from '../../../components/CircularLoader';
import MetaInfoBar from '../components/MetaInfoBar.comp';
import {useDashboard} from '../../../context/DashboardContext';
import {isSuppressedItem, isSuppressedKpi, SUPPRESSED_COPY} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {READINESS_BAND_COLORS, RISK_LEVEL_COLOR, MASTERY_BAND_COLOR} from '../constants';

import {BandChip} from '../../DashboardCommon/widgets.comp';
import {fmtValue, benchmarkText} from '../../DashboardCommon/units';
const NAVY   = '#052F44';
const ORANGE = '#FF6900';

// Truncate a score/benchmark to a whole number for display (drop decimals, per
// "truncate all decimal points"). Returns null for non-numeric input so an
// absent Grade/School avg marker stays hidden.
const truncOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;      // not sent, or under the n floor
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
};

// Secondary benchmark-marker colour (School avg) — kept distinct from NAVY
// (Grade avg) and from every tier/band fill colour used on the bars themselves.
const MARKER_SECONDARY = 'var(--brand-global-2, #7B8FC7)';
// Primary benchmark-marker colour (Grade avg). Unlike NAVY (which never
// changes for dark mode), --brand-text actually flips to a light colour in
// dark mode — these markers are plain <Box> border-colors, not Typography,
// so they're invisible to the global dark-mode Typography override and need
// their own adaptive reference or they go pitch-black-on-near-black.
const MARKER_PRIMARY = 'var(--brand-text, #052F44)';

// Grade avg = solid downward-pointing triangle above the bar; School avg =
// hollow upward-pointing triangle below it. Being permanently separated
// above/below means two markers at the same value never stack into a single
// unhoverable target.
const primaryMarkerSx = (value) => ({
    position: 'absolute', left: `${value}%`, top: '-9px',
    width: 0, height: 0,
    borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
    borderTop: `9px solid ${MARKER_PRIMARY}`,
    transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 4,
});
const secondaryMarkerSx = (value) => ({
    position: 'absolute', left: `${value}%`, bottom: '-9px',
    width: 0, height: 0,
    borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
    borderBottom: `9px solid ${MARKER_SECONDARY}`,
    transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 4,
});

const getBandColor = (band) => {
    if (!band) return '#A5A5A5';
    if (READINESS_BAND_COLORS[band]) return READINESS_BAND_COLORS[band];
    if (RISK_LEVEL_COLOR[band]) return RISK_LEVEL_COLOR[band];
    if (MASTERY_BAND_COLOR[band]) return MASTERY_BAND_COLOR[band];
    
    const lowerBand = band.toLowerCase();
    if (lowerBand.includes('concern') || lowerBand.includes('below')) {
        return '#E04535'; // Priority Concern / below-threshold band
    }
    if (lowerBand === 'low') {
        return '#E8730C'; // Low readiness band
    }
    return '#A5A5A5';
};

/* ── Sub-components ─────────────────────────────────────────────── */
const KpiCard = ({label, value, tier, tierColor, topColor, addInfos, extra}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%',
        display: 'flex', flexDirection: 'column',
    }}>
        {topColor && <Box sx={{backgroundColor: topColor, height: '6px', flexShrink: 0}}/>}
        <Box sx={{p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', justifyContent: 'center', flex: 1}}>
            <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 1}}>
                {label}
            </Typography>
            <Typography sx={{fontSize: {xs: '32px', md: '40px'}, fontWeight: 800, color: NAVY, lineHeight: 1}}>
                {value}
            </Typography>
            {tier && (
                <Box sx={{mt: 1.5}}>
                    <Box sx={{
                        display: 'inline-block', backgroundColor: tierColor, color: '#ffffff',
                        fontSize: '12px', fontWeight: 700, px: 1.5, py: 0.4, borderRadius: '20px',
                        whiteSpace: 'nowrap', textAlign: 'center', minWidth: '85px',
                    }}>
                        {tier}
                    </Box>
                </Box>
            )}
            {addInfos && addInfos.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2.5, mt: 1.5 }}>
                    {addInfos.map((info) => {
                        let val = info.info_value;
                        if (val != null && typeof val === 'number') {
                            val = Math.trunc(val);
                        }
                        if (val == null || val === '') {
                            val = '—';                                   // a comparator under the n floor (suppressed), or not sent
                        } else if (label?.toUpperCase().includes('RISK') || label?.toUpperCase().includes('PARTICIPATION')) {
                            val = `${val}%`;
                        }
                        return (
                            <Box key={info.info_name} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                <Typography sx={{ fontSize: '11px', color: '#6A7282', fontWeight: 500 }}>
                                    {info.info_name}
                                </Typography>
                                <Typography sx={{ fontSize: '14px', fontWeight: 800, color: NAVY }}>
                                    {val}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}
            {extra && (
                <Typography sx={{fontSize: '13px', color: '#6A7282', mt: 1}}>{extra}</Typography>
            )}
        </Box>
    </Box>
);

// Legend for the bar colors used below — every row's bar is colored by its
// own band (READINESS_BAND_COLORS), not one fixed "Class score" swatch, so
// the legend should show all the bands that actually appear in the chart
// rather than a single color that doesn't match most of the rows.
const READINESS_BAND_ORDER = ['High', 'Moderate', 'Basic', 'Low'];
const BandLegendChips = () => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
        {READINESS_BAND_ORDER.map((band) => (
            <Box key={band} sx={{
                backgroundColor: READINESS_BAND_COLORS[band], color: '#fff',
                fontSize: '11px', fontWeight: 700, px: 1.2, py: 0.3, borderRadius: '20px', whiteSpace: 'nowrap',
            }}>
                {band}
            </Box>
        ))}
    </Box>
);

// An area under the n floor (suppressed) keeps its label and shows the placeholder in place of bar, score and band.
const AreaScoreRow = ({label, score, tier, gradeAvg, schoolAvg, suppressed}) => {
    const tierColor = READINESS_BAND_COLORS[tier] || ORANGE;
    if (suppressed) {
        return <Box sx={{mb: 2.5}}><SuppressedValue label={label}/></Box>;
    }
    return (
        <Box sx={{mb: 2.5}}>
            <Typography sx={{fontSize: '13px', color: '#374151', mb: 0.8}}>{label}</Typography>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                <Box sx={{position: 'relative', flexGrow: 1, height: '12px', backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', borderRadius: '6px'}}>
                    <Tooltip title={fmtValue(score)} arrow placement="top">
                        <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: `${score ?? 0}%`, backgroundColor: tierColor, borderRadius: '6px', cursor: 'pointer'}}/>
                    </Tooltip>
                    {gradeAvg != null && (
                        <Tooltip title={benchmarkText('Grade avg', gradeAvg)} arrow placement="top">
                            <Box sx={primaryMarkerSx(gradeAvg)}/>
                        </Tooltip>
                    )}
                    {schoolAvg != null && (
                        <Tooltip title={benchmarkText('School avg', schoolAvg)} arrow placement="bottom">
                            <Box sx={secondaryMarkerSx(schoolAvg)}/>
                        </Tooltip>
                    )}
                </Box>
                <Typography sx={{fontSize: '20px', fontWeight: 800, color: NAVY, minWidth: '35px', textAlign: 'right'}}>{score ?? '—'}</Typography>
                {tier && <BandChip label={tier} color={tierColor}/>}
            </Box>
        </Box>
    );
};

const GuidanceBox = ({text}) => (
    <Box sx={{
        border: '1px solid #C7D9E8', borderLeft: `4px solid ${NAVY}`, borderRadius: '8px',
        backgroundColor: 'var(--brand-surface-subtle, #F0F6FB)', p: 2.5,
    }}>
        <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, mb: 1}}>
            Decision guidance:
        </Typography>
        <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>
            {text}
        </Typography>
    </Box>
);

const CLASS_SUMMARY_SECTION_CODE = 'S_2';
const CARD_SECTIONS = [CLASS_SUMMARY_SECTION_CODE];

const SummarySection = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTIONS });

    if (loading) {
        return (
            <ClassDashboardShell>
                <CircularLoader />
            </ClassDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <ClassDashboardShell>
                <NoDataAvailable />
            </ClassDashboardShell>
        );
    }

    // Defensively locate section S_2
    let sectionData = null;
    if (dashboardData.section_code === CLASS_SUMMARY_SECTION_CODE) {
        sectionData = dashboardData;
    } else if (dashboardData.cards?.sections) {
        sectionData = dashboardData.cards.sections.find(s => s.section_code === CLASS_SUMMARY_SECTION_CODE);
    } else if (Array.isArray(dashboardData.sections)) {
        sectionData = dashboardData.sections.find(s => s.section_code === CLASS_SUMMARY_SECTION_CODE);
    } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
        sectionData = dashboardData;
    }

    if (!sectionData) {
        return (
            <ClassDashboardShell>
                <NoDataAvailable />
            </ClassDashboardShell>
        );
    }

    const cards = sectionData?.cards || [];

    // C_1: TitleCard
    const titleCard = cards.find(c => c.card_type === 'TitleCard') || {};
    const titleText = titleCard.title || 'Class Baseline Overview';
    const subTitleText = titleCard.footer || 'How is my class doing overall?';

    // C_2: ORGCard
    const orgCard = cards.find(c => c.card_type === 'ORGCard') || {};
    const metaItems = (orgCard.table_objects || []).map(obj => ({
        label: obj.column_name,
        value: obj.values?.[0] || ''
    }));

    // C_3, C_3a, C_4: KPICards
    const kpiCards = cards.filter(c => c.card_type === 'KPICard') || [];
    // The "Any Cyber Risk" KPI card is intentionally excluded from this row.
    const kpiData = kpiCards.filter(k => !k.kpi_name?.toUpperCase().includes('RISK')).map(k => {
        // A KPI under the n floor (suppressed) reads "—" with the reason underneath and no band.
        const suppressed = isSuppressedKpi(k);
        let val = suppressed ? null : k.kpi_value;
        if (val != null && typeof val === 'number') {
            val = Math.trunc(val);
        }
        if (val == null || val === '') {
            val = '—';
        } else if (k.kpi_name?.toUpperCase().includes('RISK') || k.kpi_name?.toUpperCase().includes('PARTICIPATION')) {
            val = `${val}%`;
        }
        const tierColor = suppressed ? '#D1D5DB' : getBandColor(k.kpi_band);

        const isParticipation = k.kpi_name?.toUpperCase().includes('PARTICIPATION');
        // Assessed / registered (Data Map CD-07-03): kpi_data carries the counts,
        // with the "Assessed / Registered" add-info as the fallback.
        const pd = k.kpi_data || {};
        const assessedInfo = (k.kpi_add_infos || []).find(i => /assessed/i.test(i?.info_name || ''))?.info_value;
        const assessedText = pd.participants != null && pd.total != null
            ? `${Math.trunc(pd.participants)}/${Math.trunc(pd.total)}`
            : (assessedInfo || null);
        return {
            label: k.kpi_name,
            value: val,
            tier: suppressed ? null : k.kpi_band,
            tierColor,
            topColor: tierColor,
            addInfos: isParticipation || suppressed ? [] : (k.kpi_add_infos || []),
            extra: suppressed ? SUPPRESSED_COPY : (isParticipation ? assessedText : null),
        };
    });

    // C_5: BarChartBMCard
    const chartCard = cards.find(c => c.card_type === 'BarChartBMCard') || {};
    const benchmarks = chartCard.benchmarks || [];

    const areaScores = (chartCard.items || []).map(item => {
        const gradeAvgObj = item.benchmark_values?.find(b => b.name?.toLowerCase().includes('grade') || b.data?.token === 'grade');
        const schoolAvgObj = item.benchmark_values?.find(b => b.name?.toLowerCase().includes('school') || b.data?.token === 'school');
        const suppressed = isSuppressedItem(item);                 // under the n floor: placeholder row
        return {
            label: item.label,
            suppressed,
            score: suppressed ? null : truncOrNull(item.value),
            tier: suppressed ? null : item.band,
            gradeAvg: gradeAvgObj ? truncOrNull(gradeAvgObj.value) : null,
            schoolAvg: schoolAvgObj ? truncOrNull(schoolAvgObj.value) : null,
        };
    });

    // C_6: LeftLineCard (Decision guidance)
    const leftlineCard = cards.find(c => c.card_type === 'LeftLineCard') || {};
    
    // Dynamic text formatting for Decision Guidance
    const className = orgCard.table_objects?.find(o => o.column_name === 'CLASS')?.values?.[0] || 'this class';
    
    const compositeCard = kpiCards.find(k => k.kpi_name?.toUpperCase().includes('COMPOSITE')) || {};
    const compositeSuppressed = isSuppressedKpi(compositeCard) || compositeCard.kpi_value == null;
    const compositeScore = compositeSuppressed ? null : Math.trunc(compositeCard.kpi_value);
    const compositeBand = compositeSuppressed ? null : compositeCard.kpi_band;

    // Extract grade avg and school avg from compositeCard's kpi_add_infos.
    // A missing average stays null — it used to default to 43, so the guidance
    // compared the class against an average that doesn't exist.
    const compositeAddInfos = compositeCard.kpi_add_infos || [];
    const compGradeAvgObj = compositeAddInfos.find(info => info.info_name?.toLowerCase().includes('grade'));
    const compSchoolAvgObj = compositeAddInfos.find(info => info.info_name?.toLowerCase().includes('school'));
    // A comparator under the n floor (suppressed) or not sent stays null.
    const gradeAvg = compGradeAvgObj && compGradeAvgObj.info_value != null ? Math.trunc(compGradeAvgObj.info_value) : null;
    const schoolAvg = compSchoolAvgObj && compSchoolAvgObj.info_value != null ? Math.trunc(compSchoolAvgObj.info_value) : null;

    // Find weakest area — among the areas that show a score.
    const itemsList = (chartCard.items || []).filter((it) => !isSuppressedItem(it) && it.value != null);
    const sortedItems = [...itemsList].sort((a, b) => a.value - b.value);
    const weakestArea = sortedItems[0]?.label || 'the lowest indicators';

    const participationCard = kpiCards.find(k => k.kpi_name?.toUpperCase().includes('PARTICIPATION')) || {};
    const participation = Math.trunc(participationCard.kpi_value || 0);

    // Adaptive comparison text — only against the averages the data actually has.
    const relation = (avg) => (compositeScore < avg ? 'below' : (compositeScore > avg ? 'above' : 'at'));
    let comparisonText = '';
    if (gradeAvg != null && schoolAvg != null) {
        const g = relation(gradeAvg);
        const s = relation(schoolAvg);
        comparisonText = g === s
            ? `, ${g} the grade (${gradeAvg}) and school (${schoolAvg})`
            : `, ${g} the grade (${gradeAvg}) and ${s} the school (${schoolAvg})`;
    } else if (gradeAvg != null) {
        comparisonText = `, ${relation(gradeAvg)} the grade (${gradeAvg})`;
    } else if (schoolAvg != null) {
        comparisonText = `, ${relation(schoolAvg)} the school (${schoolAvg})`;
    }

    const participationWarning = participation < 75
        ? ` — but confirm participation first, because coverage is only ${participation}%.`
        : '.';
    const guidanceText = compositeSuppressed
        ? `Scores for Class ${className} are not shown — fewer than 10 students completed the assessment (coverage ${participation}%). Raise participation before planning lessons from these results.`
        : `Class ${className} sits at ${compositeScore}${compositeBand ? ` (${compositeBand})` : ''}${comparisonText}. Start with ${weakestArea} and the lowest indicators${participationWarning}`;

    return (
        <ClassDashboardShell>
            <Box sx={{mb: 2}}>
                <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.2, mb: 0.3}}>
                    {titleText}
                </Typography>
                <Typography sx={{fontSize: '14px', color: '#4B5563', fontStyle: 'italic', mb: 0.8}}>
                    {subTitleText}
                </Typography>
            </Box>
            <MetaInfoBar items={metaItems}/>

            <Grid container spacing={2.5} sx={{mb: 3}}>
                {kpiData.map((k) => (
                    <Grid item xs={12} sm={12 / kpiData.length} key={k.label}>
                        <KpiCard {...k}/>
                    </Grid>
                ))}
            </Grid>

            {/* COS Area Scores — widened to full width now that Needs Attention is gone */}
            <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, mb: 2.5}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5}}>
                    <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>
                        {chartCard.title || 'Area Scores vs Grade & School'}
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap'}}>
                        <BandLegendChips/>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${MARKER_PRIMARY}`}}/>
                            <Typography sx={{fontSize: '11px', color: '#6A7282'}}>{(benchmarks.find(b => b.name?.toLowerCase().includes('grade'))?.name || 'Grade avg').replace(/\.$/, '')}</Typography>
                        </Box>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
                            <Typography sx={{fontSize: '11px', color: '#6A7282'}}>{(benchmarks.find(b => b.name?.toLowerCase().includes('school'))?.name || 'School avg').replace(/\.$/, '')}</Typography>
                        </Box>
                    </Box>
                </Box>
                {areaScores.map((a) => (
                    <AreaScoreRow
                        key={a.label}
                        {...a}
                    />
                ))}
            </Box>

            <GuidanceBox text={guidanceText}/>
        </ClassDashboardShell>
    );
};

export default SummarySection;
